import React, { useCallback, useContext, useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { KeyValue } from "@/main/models/common";
import { DynamicReport_VariableResponse } from "@/main/models/response";
import { VariableTypeEnum } from "@/main/services/enum/VariableTypeEnum";
import VariableTypeBodyhop from "./variableTypeBodyshop";
import { IFilterDinamic, IFormFiltersDynamicExport } from "../modalExportDynamicReport";
import VariableTypeBoolean from "./variableTypeBoolean";
import VariableTypeInsurer from "./variableTypeInsurer";
import VariableTypeSupplier from "./variableTypeSupplier";
import VariableTypeCountry from "./variableTypeCountry";
import VariableTypeFreeText from "./variableTypeFreeText";
import VariableTypeFreeInteger from "./variableTypeFreeInteger";
import VariableTypeFreeDecimal from "./variableTypeFreeDecimal";
import VariableTypeOrderStatus from "./variableTypeOrderStatus";
import VariableTypeDate from "./variableTypeDate";
import VariableTypeRangeDate from "./variableTypeRangeDate";
import VariableTypeMake from "./variableTypeMake";
import VariableTypeModel from "./variableTypeModel";
import VariableTypeSubModel from "./variableTypeSubModel";
import VariableTypeYear from "./variableTypeYear";
import VariableTypeState from "./variableTypeState";

type IdsKeyReducer = "id-mappingCodes-filters" | "id-mappingCodes-modalMapCodeEdit" | "id-mappingCodes-modalMapCode";

export type DynamicFiltersExportContextType = {
	watcherCountry: KeyValue | null;
	keyReducer: IdsKeyReducer;
	idScreen: (id: string) => string;
	findFieldIndexByGuidDynamicReportVariable: (guidDynamicReportVariable: string) => number;
};
export interface GeneralVariableType {
	filter: IFilterDinamic;
}

export const DynamicFiltersExportContext = React.createContext({} as DynamicFiltersExportContextType);

interface IRequiredDefaultFields {
	country: KeyValue | null;
}

interface IDynamicFiltersExportProps {
	INITIAL_FORM_STATE_DEFAULT: IRequiredDefaultFields;
	keyReducer: IdsKeyReducer;
	dataDynamicFilters: DynamicReport_VariableResponse[];
}

const RenderComponentObject: Record<number, React.ComponentType<GeneralVariableType>> = {
	[VariableTypeEnum.BodyShopList]: VariableTypeBodyhop,
	[VariableTypeEnum.InsurerList]: VariableTypeInsurer,
	[VariableTypeEnum.SupplierList]: VariableTypeSupplier,
	[VariableTypeEnum.MakeList]: VariableTypeMake,
	[VariableTypeEnum.ModelList]: VariableTypeModel,
	[VariableTypeEnum.SubModelList]: VariableTypeSubModel,
	[VariableTypeEnum.VehicleYearList]: VariableTypeYear,
	[VariableTypeEnum.CountryList]: VariableTypeCountry,
	[VariableTypeEnum.Boolean]: VariableTypeBoolean,
	[VariableTypeEnum.OrderStatusList]: VariableTypeOrderStatus,
	[VariableTypeEnum.FreeText]: VariableTypeFreeText,
	[VariableTypeEnum.FreeInteger]: VariableTypeFreeInteger,
	[VariableTypeEnum.FreeDecimal]: VariableTypeFreeDecimal,
	[VariableTypeEnum.Date]: VariableTypeDate,
	[VariableTypeEnum.DateRange]: VariableTypeRangeDate,
	[VariableTypeEnum.StatesList]: VariableTypeState,
};

function DynamicFiltersExport({ dataDynamicFilters, keyReducer }: IDynamicFiltersExportProps) {
	const { control } = useFormContext<IFormFiltersDynamicExport>();
	const { fields, replace } = useFieldArray({
		control,
		name: "filtersDinamics",
	});
	const watcherCountry = useWatch({ control, name: "country" });

	const findFieldIndexByGuidDynamicReportVariable = useCallback(
		(guidDynamicReportVariable: string) => {
			return fields.findIndex((field) => field.guidDynamicReportVariable === guidDynamicReportVariable);
		},
		[fields],
	);

	function renderFilters(filter: IFilterDinamic) {
		const Component = RenderComponentObject[filter.idVariableType as VariableTypeEnum];

		if (!Component) return null;

		const propsGeneral: GeneralVariableType = {
			filter,
		};
		return <Component key={filter.guidDynamicReportVariable} {...propsGeneral} />;
	}
	const idScreen = (id: string) => `${keyReducer}-${id}`;

	useEffect(() => {
		const formattedFields = dataDynamicFilters.map((filter) => {
			const INITIAL_VALUE = {
				[VariableTypeEnum.BodyShopList]: null,
				[VariableTypeEnum.InsurerList]: null,
				[VariableTypeEnum.SupplierList]: null,
				[VariableTypeEnum.MakeList]: null,
				[VariableTypeEnum.ModelList]: null,
				[VariableTypeEnum.SubModelList]: null,
				[VariableTypeEnum.VehicleYearList]: null,
				[VariableTypeEnum.CountryList]: null,
				[VariableTypeEnum.Boolean]: false,
				[VariableTypeEnum.OrderStatusList]: null,
				[VariableTypeEnum.FreeText]: "",
				[VariableTypeEnum.FreeInteger]: "",
				[VariableTypeEnum.FreeDecimal]: "",
				[VariableTypeEnum.Date]: null,
				[VariableTypeEnum.DateRange]: null,
				[VariableTypeEnum.StatesList]: null,
			};
			const valueFormmated = INITIAL_VALUE[filter.idVariableType as VariableTypeEnum];
			const formattedFields: IFilterDinamic = {
				guidDynamicReportVariable: filter.guid,
				idVariableType: filter.idVariableType,
				value: valueFormmated,
				valueRange: valueFormmated,
				responseServer: filter,
			};

			return formattedFields;
		});

		replace(formattedFields);
	}, [JSON.stringify(dataDynamicFilters)]);

	return (
		<DynamicFiltersExportContext.Provider
			value={{
				keyReducer,
				watcherCountry,
				findFieldIndexByGuidDynamicReportVariable,
				idScreen,
			}}
		>
			{fields.map(renderFilters)}
		</DynamicFiltersExportContext.Provider>
	);
}

export const useDynamicFiltersExport = () => useContext(DynamicFiltersExportContext);

export default DynamicFiltersExport;
